import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
export function useOverflowNav(primaryRef, options = {}) {
  const {
    overflowOrder = [],
    moreButtonWidth = 70
  } = options;
  const hiddenItems = ref(new Set());
  const isCompact = ref(false);
  const itemWidths = new Map();
  let maxFixedWidth = 0;
  let navExtraWidth = 0;
  let resizeObserver = null;
  let debounceTimer = null;
  let retryInterval = null;
  let measuring = false;
  let rebalancePending = false;
  const measure = () => {
    const primary = primaryRef.value;
    if (!primary) return false;
    const nav = primary.querySelector('.link-group.center');
    if (!nav) return false;
    const navStyle = getComputedStyle(nav);
    for (const id of overflowOrder) {
      const el = nav.querySelector(`[data-nav-id="${id}"]`);
      const w = el?.offsetWidth || 0;
      // Only update when visible (w > 0) so we don't overwrite a cached width
      // with 0 when the item is hidden via v-show.
      if (el && w > 0) {
        itemWidths.set(id, w);
      }
    }
    let navFixed = 0;
    for (const child of nav.children) {
      if (!('navFixed' in (child.dataset || {}))) continue;
      if (child.offsetWidth > 0) {
        navFixed += child.offsetWidth;
      }
    }
    navExtraWidth = parseFloat(navStyle.marginLeft || 0) + parseFloat(navStyle.marginRight || 0) + parseFloat(navStyle.paddingLeft || 0) + parseFloat(navStyle.paddingRight || 0) + navFixed;
    let fixed = 0;
    for (const child of primary.children) {
      if (child === nav) continue;
      const pos = getComputedStyle(child).position;
      if (pos === 'absolute' || pos === 'fixed') continue;
      if (child.offsetWidth > 0) {
        fixed += child.offsetWidth;
      }
    }
    if (fixed > maxFixedWidth) maxFixedWidth = fixed;
    return fixed;
  };
  const setsEqual = (a, b) => {
    if (a.size !== b.size) return false;
    for (const item of a) {
      if (!b.has(item)) return false;
    }
    return true;
  };
  const recalculate = currentFixed => {
    if (!primaryRef.value) return;
    if (itemWidths.size === 0) return;
    const primaryWidth = primaryRef.value.clientWidth;
    const fixedWidth = currentFixed || maxFixedWidth;
    let totalNeeded = 0;
    for (const w of itemWidths.values()) {
      totalNeeded += w;
    }

    // All items fit even with the firm name visible — full non-compact state.
    const conservativeAvailable = primaryWidth - maxFixedWidth - navExtraWidth;
    if (totalNeeded <= conservativeAvailable) {
      if (hiddenItems.value.size > 0 || isCompact.value) {
        hiddenItems.value = new Set();
        isCompact.value = false;
      }
      return;
    }

    // Items don't fit with firm name. Check against the current (possibly compact) right group.
    const baseAvailable = primaryWidth - fixedWidth - navExtraWidth;

    // All items fit without needing a More button. Keep firm name hidden to avoid
    // oscillation (showing firm name would overflow again), but show every nav item.
    if (totalNeeded <= baseAvailable) {
      if (hiddenItems.value.size > 0) {
        hiddenItems.value = new Set();
      }
      if (!isCompact.value) {
        isCompact.value = true;
      }
      return;
    }

    // Some items must go into More.
    let available = baseAvailable - moreButtonWidth;
    const newHidden = new Set();
    let used = 0;
    const reversed = [...overflowOrder].reverse();
    const fits = new Set();
    for (const id of reversed) {
      const w = itemWidths.get(id);
      if (!w) continue;
      if (used + w <= available) {
        fits.add(id);
        used += w;
      }
    }
    for (const id of overflowOrder) {
      if (itemWidths.has(id) && !fits.has(id)) {
        newHidden.add(id);
      }
    }
    if (!setsEqual(hiddenItems.value, newHidden)) {
      const enteringCompact = !isCompact.value && newHidden.size > 0;
      hiddenItems.value = newHidden;
      isCompact.value = true;

      // Entering compact hides the firm name, shrinking the right group. The
      // ResizeObserver won't catch this internal change, so schedule one re-evaluation
      // to reclaim that freed space for nav items.
      if (enteringCompact && !rebalancePending) {
        rebalancePending = true;
        setTimeout(() => {
          rebalancePending = false;
          const cf = measure();
          recalculate(cf);
        }, 20);
      }
    }
  };
  const update = () => {
    if (measuring) return;
    measuring = true;
    const currentFixed = measure();
    recalculate(currentFixed);
    measuring = false;
  };
  const debouncedUpdate = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(update, 80);
  };
  onMounted(async () => {
    await nextTick();
    await nextTick();
    measure();
    let retries = 0;
    retryInterval = setInterval(() => {
      retries++;
      const currentFixed = measure();
      recalculate(currentFixed);
      if (itemWidths.size >= overflowOrder.length || retries >= 5) {
        clearInterval(retryInterval);
      }
    }, 500);
    recalculate(maxFixedWidth);
    if (primaryRef.value) {
      resizeObserver = new ResizeObserver(debouncedUpdate);
      resizeObserver.observe(primaryRef.value);
    }
  });
  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    clearTimeout(debounceTimer);
    clearInterval(retryInterval);
  });
  return {
    hiddenItems,
    isCompact
  };
}